import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { environment } from '@/environments/environment';

export interface InquiryUiActionSelection {
  type: string;
  selectedId?: string;
}

export interface InquiryStreamV2Request {
  inquiryId: string;
  inquiryContent: string;
  uploadedFileId?: string;
  fileType?: string;
  uiActionSelection?: InquiryUiActionSelection;
}

// Wire format is camelCase (confirmed from the live SSE payload), e.g.
// {"type":"ui_action","actions":[{"actionType":"plantation.request_selection","reason":"..."}]}
export interface InquiryUiAction {
  actionType: string;
  reason?: string;
}

export type InquiryStreamV2Event =
  | { type: 'metadata'; session_id?: string; user_id?: string; turn_id?: number; timing?: { total_time?: number } }
  | { type: 'chunk'; content: string }
  | { type: 'ui_action'; actions: InquiryUiAction[] }
  | { type: 'reference'; reference: string }
  | { type: 'error'; message?: string }
  | { type: 'done' };

// Using fetch + ReadableStream here (instead of Angular HttpClient) because
// HttpClient is backed by XMLHttpRequest, which coalesces small SSE chunks
// into a single progress event on most browsers. fetch exposes the raw byte
// stream and yields each SSE chunk as it arrives on the wire.
//
// This targets the v2 correspondence stream endpoint, whose event union
// differs from v1: `ui_action` (AI-driven data selection requests) and a
// type-less `reference` event (detected by the presence of a `reference`
// key rather than a `type` discriminant) are v2-only additions.
@Injectable({ providedIn: 'root' })
export class InquiryStreamV2Service {
  private readonly endpoint = `${environment.apiBaseUrl}/v4.0/inquiry/correspondence/stream/v2`;

  stream(request: InquiryStreamV2Request): Observable<InquiryStreamV2Event> {
    return new Observable<InquiryStreamV2Event>((subscriber) => {
      const controller = new AbortController();

      this.runStream(request, controller, subscriber);

      return () => controller.abort();
    });
  }

  private async runStream(
    request: InquiryStreamV2Request,
    controller: AbortController,
    subscriber: Subscriber<InquiryStreamV2Event>
  ): Promise<void> {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        subscriber.next({
          type: 'error',
          message: `HTTP ${response.status} ${response.statusText}`,
        });
        subscriber.complete();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        buffer = this.drainBuffer(buffer, subscriber);
      }

      const tail = (buffer + decoder.decode()).trim();
      if (tail.length > 0) {
        this.dispatchRawEvent(tail, subscriber);
      }

      subscriber.complete();
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        subscriber.complete();
        return;
      }
      const message = error instanceof Error ? error.message : 'Stream failed';
      subscriber.next({ type: 'error', message });
      subscriber.complete();
    }
  }

  private buildHeaders(): HeadersInit {
    const emptyGuid = '00000000-0000-0000-0000-000000000000';
    const languageGuid =
      localStorage.getItem('languageGuid') ?? '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3';
    const isCsiDomain = location.hostname.includes('sustainability.doktar');
    const appId = !isCsiDomain ? environment.appId : environment.appId_csi;

    return {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-App': appId,
      'X-Enterprise': localStorage.getItem('enterpriseId') ?? emptyGuid,
      'X-Auth-Token': 'Bearer ' + (localStorage.getItem('token') ?? ''),
      'X-Locale': languageGuid,
    };
  }

  private drainBuffer(
    buffer: string,
    subscriber: Subscriber<InquiryStreamV2Event>
  ): string {
    let cursor = 0;
    while (cursor < buffer.length) {
      const boundary = this.findEventBoundary(buffer, cursor);
      if (boundary === -1) {
        break;
      }
      const rawEvent = buffer.slice(cursor, boundary.start);
      cursor = boundary.end;
      this.dispatchRawEvent(rawEvent, subscriber);
    }
    return buffer.slice(cursor);
  }

  private findEventBoundary(
    text: string,
    fromIndex: number
  ): { start: number; end: number } | -1 {
    const lf = text.indexOf('\n\n', fromIndex);
    const crlf = text.indexOf('\r\n\r\n', fromIndex);

    if (lf === -1 && crlf === -1) {
      return -1;
    }
    if (lf !== -1 && (crlf === -1 || lf < crlf)) {
      return { start: lf, end: lf + 2 };
    }
    return { start: crlf, end: crlf + 4 };
  }

  private dispatchRawEvent(
    rawEvent: string,
    subscriber: Subscriber<InquiryStreamV2Event>
  ): void {
    const lines = rawEvent.split(/\r?\n/);
    const dataParts: string[] = [];

    for (const line of lines) {
      if (line.startsWith('data:')) {
        dataParts.push(line.slice(5).trimStart());
      }
    }

    if (dataParts.length === 0) {
      return;
    }

    const payload = dataParts.join('\n').trim();
    if (!payload) {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch {
      // Ignore malformed payloads; backend should always emit valid JSON.
      return;
    }

    if (!parsed || typeof parsed !== 'object') {
      return;
    }

    const record = parsed as Record<string, unknown>;

    // `reference` events carry no `type` discriminant - detect by key presence.
    if (typeof record['type'] !== 'string' && 'reference' in record) {
      subscriber.next({ type: 'reference', reference: record['reference'] as string });
      return;
    }

    if (typeof record['type'] === 'string') {
      subscriber.next(record as unknown as InquiryStreamV2Event);
    }
  }
}
